import { useState } from 'react'
import { Smartphone, X } from 'lucide-react'
import { detectCaptureClient } from '@/lib/capture-heartbeat'
import { APP_STORE_URL } from '@/lib/app-store'

/**
 * Steer iPhone-browser users to the app before they record (CLA-1341).
 *
 * Every "recording may have stopped capturing" alert in the first CLA-1248
 * batch that had a web client was an iPhone in Safari: the OS suspends the
 * page's microphone the moment the screen locks or the clinician switches
 * apps, and until CLA-1341 nothing on screen said so. The app records in the
 * background and does not have this failure. The notice does not block
 * recording — someone mid-consult with no time to install still records —
 * it just makes the better path visible before the first tap.
 *
 * "Not now" snoozes for two weeks per device; a clinician who has decided to
 * stay in the browser is not nagged every visit.
 */
const SNOOZE_KEY = 'claro.ios-browser-notice.snoozedUntil'
const SNOOZE_MS = 14 * 24 * 60 * 60 * 1000

function snoozed(): boolean {
  try {
    const until = Number(localStorage.getItem(SNOOZE_KEY))
    return Number.isFinite(until) && until > Date.now()
  } catch {
    return false
  }
}

export function IosBrowserRecordingNotice() {
  const [hidden, setHidden] = useState(() => detectCaptureClient() !== 'web_ios' || snoozed())
  if (hidden) return null

  const dismiss = () => {
    try {
      localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS))
    } catch {
      /* private mode — session-only dismissal is fine */
    }
    setHidden(true)
  }

  return (
    <div
      role="status"
      data-testid="ios-browser-notice"
      className="shrink-0 bg-claro-50 border-b border-claro-200 px-4 py-2 flex items-center gap-3"
    >
      <Smartphone className="w-4 h-4 text-claro-700 shrink-0" aria-hidden />
      <p className="flex-1 text-[13px] text-ink-secondary">
        Recording in Safari stops when your iPhone locks or you switch apps. For consults, use
        the Claro app —{' '}
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium link"
        >
          get it on the App Store
        </a>
        .
      </p>
      <button
        onClick={dismiss}
        className="p-1 rounded text-ink-faint hover:text-ink hover:bg-surface-tertiary transition-colors shrink-0"
        aria-label="Not now"
        title="Not now"
      >
        <X className="w-3.5 h-3.5" aria-hidden />
      </button>
    </div>
  )
}
